import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyOption } from './PropertySearch';
import { PropertySearch } from './PropertySearch';
import { Tag } from './Tag';
import styles from './PropertiesCell.module.css';

interface PropertiesCellProps {
  assignedProperties: PropertyOption[];
  allProperties: PropertyOption[];
  emptyLabel: string;
  isPickerOpen: boolean;
  isEditing: boolean;
  onOpenPicker: () => void;
  onClosePicker: () => void;
  onAdd: (propertyId: string) => void;
  onRemove: (propertyId: string) => void;
  // Read-only users still see the assigned tags, but nothing that adds one.
  canEdit: boolean;
}

export const PropertiesCell = ({
  assignedProperties,
  allProperties,
  emptyLabel,
  isPickerOpen,
  isEditing,
  onOpenPicker,
  onClosePicker,
  onAdd,
  onRemove,
  canEdit
}: PropertiesCellProps) => {
  const { t } = useTranslation();

  const available = useMemo(() => {
    const assigned = new Set(assignedProperties.map((rp) => rp.id));
    return allProperties.filter((rp) => !assigned.has(rp.id));
  }, [assignedProperties, allProperties]);

  return (
    <div className={`${styles.cell} ${isEditing ? styles.editing : ''}`}>
      <div className={styles.content}>
        {assignedProperties.map((rp) => (
          <Tag
            key={rp.id}
            label={rp.name}
            onRemove={isEditing ? () => onRemove(rp.id) : undefined}
            removeAriaLabel={t('remove')}
          />
        ))}
        {canEdit &&
          (isPickerOpen ? (
            <PropertySearch
              available={available}
              emptyLabel={emptyLabel}
              onSelect={(id) => {
                onAdd(id);
                onClosePicker();
              }}
              onClose={onClosePicker}
            />
          ) : (
            <button type="button" className={styles.addTrigger} onClick={onOpenPicker}>
              {t('room-needs-add')}
            </button>
          ))}
      </div>
    </div>
  );
};
